'use client';

import { useState } from 'react';
import { ChevronDown, ChevronUp, CheckCircle2, MapPin, Globe } from 'lucide-react';

const expertiseAreas = [
  {
    title: 'Vedic Astrology',
    description: 'Traditional Indian astrology based on ancient scriptures and planetary positions'
  },
  {
    title: 'Birth Chart Analysis',
    description: 'Detailed kundali reading and interpretation for life guidance'
  },
  {
    title: 'Marriage Compatibility',
    description: 'Comprehensive horoscope matching for successful relationships'
  },
  {
    title: 'Business Astrology',
    description: 'Strategic timing and guidance for business success'
  },
  {
    title: 'Health Astrology',
    description: 'Preventive health measures based on planetary influences'
  },
  {
    title: 'Spiritual Counseling',
    description: 'Personal growth and spiritual development guidance'
  }
];

const faqs = [
  {
    question: 'What is Vedic Astrology?',
    answer: 'Vedic astrology, also known as Jyotish, is an ancient Indian system of astrology that uses the positions of planets and stars to provide guidance about life events, personality traits, and future possibilities.'
  },
  {
    question: 'How accurate are your predictions?',
    answer: 'We follow traditional Vedic methods and are honest about what a chart can and cannot tell you. Our focus is practical guidance you can act on, not sensational predictions.'
  },
  {
    question: 'Do you provide services outside Nepal?',
    answer: 'Yes, we offer online consultations and digital services worldwide. We also provide special puja services at sacred temples across Nepal and India.'
  },
  {
    question: 'What information do I need for a birth chart analysis?',
    answer: 'We need your exact birth date, time, and place of birth to create an accurate birth chart. The more precise the information, the more accurate the analysis.'
  }
];

const serviceAreas = [
  { location: 'Kathmandu, Nepal', description: 'Our main service center', icon: MapPin },
  { location: 'Pashupatinath Temple', description: 'Special puja services', icon: MapPin },
  { location: 'Online consultations worldwide', description: 'Available globally', icon: Globe },
  { location: 'Holy places tours', description: 'Across Nepal and India', icon: MapPin }
];

export default function WhyChooseSection() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <section
      className="bg-gray-50 dark:bg-dark-900 w-full py-12 sm:py-16 lg:py-20"
      style={{ contentVisibility: 'auto', containIntrinsicSize: '1000px' }}
    >
      <div className="container w-full max-w-7xl mx-auto px-4">
        {/* Main Heading */}
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Why Choose Swastishanti for Your Astrological Needs?
          </h2>
          <p className="text-base sm:text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Swastishanti is Nepal&apos;s premier destination for authentic Vedic astrology services, combining ancient wisdom with modern expertise. Our certified astrologers provide comprehensive guidance for all aspects of life, from personal relationships to career decisions.
          </p>
        </div>

        {/* Our Expertise Areas */}
        <div className="mb-12 sm:mb-16">
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8 text-center">
            Our Expertise Areas
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {expertiseAreas.map((area, index) => (
              <div
                key={index}
                className="bg-white dark:bg-dark-800 rounded-lg p-5 sm:p-6 shadow-brand-sm hover:shadow-brand-md transition-all duration-300 border border-gray-200 dark:border-gray-700"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <CheckCircle2 className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                      {area.title}
                    </h4>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                      {area.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Frequently Asked Questions */}
        <div className="mb-12 sm:mb-16">
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8 text-center">
            Frequently Asked Questions
          </h3>
          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white dark:bg-dark-800 rounded-lg shadow-brand-sm border border-gray-200 dark:border-gray-700 overflow-hidden"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-5 sm:px-6 py-4 sm:py-5 text-left flex items-center justify-between hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
                >
                  <span className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white pr-4">
                    {faq.question}
                  </span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-5 sm:px-6 pb-4 sm:pb-5">
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Our Service Areas */}
        <div>
          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8 text-center">
            Our Service Areas
          </h3>
          <p className="text-center text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 max-w-2xl mx-auto">
            We proudly serve clients across Nepal and internationally, with special expertise in:
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-5xl mx-auto">
            {serviceAreas.map((area, index) => {
              const IconComponent = area.icon;
              return (
                <div
                  key={index}
                  className="bg-white dark:bg-dark-800 rounded-lg p-5 sm:p-6 shadow-brand-sm hover:shadow-brand-md transition-all duration-300 border border-gray-200 dark:border-gray-700 text-center"
                >
                  <div className="flex justify-center mb-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-full flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <h4 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {area.location}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {area.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

